import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Chip, Flex, FlexSpace } from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import useHover from '@elementary/common-ui/hooks/useHover';
import { useTheme } from '@mui/material';
import { Position, NodeProps, useEdges, useStore } from 'reactflow';
import { fontSize, radius, space } from '../../style/utils';
import { GRAPH_CHILD_NODE_HEIGHT } from './constants';
import { ReactFlowNodeData } from './types';
import { useLineageGraphContext } from './LineageGraphContext';
import GraphChildNodeCompact from './GraphChildNodeCompact';
import GraphNodeHandle from './GraphNodeHandle';
import NodeContextMenu from './NodeContextMenu';
import {
  getFilterHighlightBackground,
  isFilterMatch,
} from './utils/lineageDisplaySettings';
import { selectLineageZoomTier } from './utils/lineageGraphUtils';

const Wrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isLastChild?: boolean;
  $isOnHighlightPath?: boolean;
  $width: number;
}>`
  height: ${GRAPH_CHILD_NODE_HEIGHT}px;
  width: ${({ $width }) => $width}px;
  cursor: pointer;

  border: ${({ theme, $isHighlighted, $isOnHighlightPath }) =>
    $isHighlighted || $isOnHighlightPath
      ? `2px solid ${theme.palette.accent.secondary}`
      : 'none'};

  padding: ${({ $isHighlighted, $isOnHighlightPath }) =>
    $isHighlighted || $isOnHighlightPath ? '0' : space(0, 'xxs')};

  padding-bottom: ${({ $isHighlighted, $isOnHighlightPath, $isLastChild }) =>
    $isHighlighted || $isOnHighlightPath || !$isLastChild ? '0' : space('xxs')};

  border-radius: ${({ $isLastChild }) =>
    $isLastChild ? radius(0, 0, 'md', 'md') : 0};
`;

const InnerWrapper = styled(Flex)<{
  $isHighlighted?: boolean;
  $isLastChild?: boolean;
  $isOnHighlightPath?: boolean;
  $filterBackground?: string;
  $isMenuOpen?: boolean;
}>`
  width: 100%;
  height: 100%;

  padding: ${({ $isHighlighted, $isLastChild, $isOnHighlightPath }) =>
    !($isHighlighted || $isOnHighlightPath) && $isLastChild
      ? space('xxs', 'lg', 0, 'lg')
      : space(0, 'lg')};

  border-radius: ${({ $isLastChild }) =>
    $isLastChild ? radius(0, 0, 'sm', 'sm') : 0};

  background: ${({ theme, $isHighlighted, $filterBackground, $isMenuOpen }) =>
    $isMenuOpen
      ? theme.palette.surface.muted
      : $filterBackground
      ? $filterBackground
      : $isHighlighted
      ? theme.palette.surface.tinted
      : theme.palette.surface.base};

  &:hover {
    background: ${({ theme }) => theme.palette.surface.muted};
  }
`;

const GraphChildNodeCard = (node: NodeProps<ReactFlowNodeData>) => {
  const { id, data, dragging } = node;
  const edges = useEdges();

  const hasIncomingEdges = edges.some((e) => e.target === id && !e.hidden);
  const hasOutgoingEdges = edges.some((e) => e.source === id && !e.hidden);

  const { highlightedNode, isControlled } = useLineageGraphContext();
  const theme = useTheme();

  const ref = useRef<any>();
  const isHover = useHover(ref);

  const [menuPos, setMenuPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const handleMenuOpen = (e: any) => {
    if (isControlled) return;
    e.stopPropagation();
    e.preventDefault();
    setMenuPos({ left: e.clientX, top: e.clientY });
  };

  const handleMenuClose = () => {
    setMenuPos(null);
  };

  const isLastChild = useMemo(() => {
    const allSiblings = data.parentNode?.children;
    if (allSiblings) {
      return id === allSiblings[allSiblings.length - 1].id;
    }
    return false;
  }, [data, id]);

  const isChildOfChildNode = !!node.data.parentNode?.parent;

  return (
    <Wrapper
      ref={ref}
      $isHighlighted={highlightedNode?.id === id}
      $isLastChild={isLastChild}
      $isOnHighlightPath={data.isOnHighlightPath}
      $width={data.width}
      onContextMenu={isControlled ? undefined : handleMenuOpen}
    >
      <InnerWrapper
        $isHighlighted={highlightedNode?.id === id}
        $isLastChild={isLastChild}
        $isOnHighlightPath={data.isOnHighlightPath}
        $filterBackground={
          isFilterMatch(data)
            ? getFilterHighlightBackground(theme.palette)
            : undefined
        }
        $isMenuOpen={!!menuPos}
      >
        <GraphNodeHandle
          type="source"
          position={Position.Right}
          $extendHandle={hasOutgoingEdges && isChildOfChildNode}
          $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
          $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
        />

        <FlexSpace
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <OverflowTooltip
            sx={{
              fontSize: fontSize('base'),
            }}
            color="content.primary"
            bold={highlightedNode?.id === id}
          >
            {data.metadata.label}
          </OverflowTooltip>

          {(!!data.totals?.errors ||
            !!data.totals?.warnings ||
            !!data.totals?.passed ||
            !!data.totals?.failures) && (
            <Flex sx={{ alignItems: 'center', gap: 0.6, ml: 3 }}>
              {data.totals?.passed > 0 && (
                <Chip
                  role="alert"
                  aria-label="success"
                  label={data.totals.passed}
                  color="success"
                  size="small"
                />
              )}
              {data.totals?.warnings > 0 && (
                <Chip
                  role="alert"
                  aria-label="warning"
                  label={data.totals.warnings}
                  color="warning"
                  size="small"
                />
              )}
              {(data.totals?.errors > 0 || data.totals?.failures > 0) && (
                <Chip
                  role="alert"
                  aria-label="error"
                  label={
                    (data.totals.errors || 0) + (data.totals.failures || 0)
                  }
                  color="error"
                  size="small"
                />
              )}
            </Flex>
          )}
        </FlexSpace>

        {!isControlled && (
          <NodeContextMenu
            showMenuButton={isHover && !dragging}
            node={node}
            pos={menuPos}
            onOpen={handleMenuOpen}
            onClose={handleMenuClose}
            onHighlightNodeClick={() => ref.current?.click()}
          />
        )}

        <GraphNodeHandle
          type="target"
          position={Position.Left}
          $extendHandle={hasIncomingEdges && isChildOfChildNode}
          $isHighlighted={id === highlightedNode?.id || data.isOnHighlightPath}
          $nodeHeight={GRAPH_CHILD_NODE_HEIGHT}
        />
      </InnerWrapper>
    </Wrapper>
  );
};

const GraphChildNode = (node: NodeProps<ReactFlowNodeData>) => {
  const { isControlled } = useLineageGraphContext();
  const zoomTier = useStore(selectLineageZoomTier);

  if (isControlled || zoomTier === 'card') {
    return <GraphChildNodeCard {...node} />;
  }

  return <GraphChildNodeCompact {...node} />;
};

export default GraphChildNode;
